/**
 * DemoPanel — the "Live demo" chat card in the landing's second section.
 *
 * The state machine is a direct port of `runDemo()` in the design file's
 * `DCLogic` class, including every wait in milliseconds:
 *
 *   reset → 800 → type the question one char per 32ms → 500 → commit the user
 *   bubble → 400 → thinking dots → 1100 → reply → 5400 → next scenario
 *
 * All of them are divided by `demoSpeed` (the design exposes it as a 0.5-2×
 * prop, default 1). The two scenarios alternate, and the SECOND one has no
 * product card on purpose: it is the design demonstrating the product's honesty
 * rule, where an answer that stock cannot back is badged "General advice"
 * instead of being dressed up as a match. That contrast is the point of the
 * section, so both scenarios ship.
 *
 * Reduced motion / no autoplay: the design's own `demoAutoplay === false`
 * branch renders the settled state (`showUser`, `replied`) with no typing and
 * no loop. That is exactly the right reduced-motion behaviour, so it is reused
 * rather than invented — the card still shows a real answered exchange, it just
 * does not animate.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";

interface Scenario {
  q: string;
  a: string;
  card: { name: string; price: string; stall: string } | null;
}

const SCENARIOS: readonly Scenario[] = [
  {
    q: "My estimate says 40 bags of floor leveler for 62 m² — is that right?",
    a: "At 8 mm over 62 m² you need about 26 bags of 25 kg — 40 is oversized. Here is the fair option:",
    card: {
      name: "Self-leveling floor mix, 25 kg",
      price: "620 сом / bag",
      stall: "Дордой · container 14-32",
    },
  },
  {
    q: "Porcelain or ceramic tile for a bathroom floor?",
    a: "Porcelain — lower water absorption and a harder surface. Verified sellers list it from 780 сом/m². Send your sizes and I’ll match exact stock.",
    card: null,
  },
];

interface DemoState {
  typed: string;
  showUser: boolean;
  thinking: boolean;
  replied: boolean;
  scen: number;
}

const SETTLED: DemoState = {
  typed: "",
  showUser: true,
  thinking: false,
  replied: true,
  scen: 0,
};

export interface DemoPanelProps {
  /** `window` prop `demoAutoplay`, default true. */
  autoplay?: boolean;
  /** `window` prop `demoSpeed`, 0.5–2, default 1. */
  speed?: number;
}

export function DemoPanel({
  autoplay = true,
  speed = 1,
}: DemoPanelProps): ReactElement {
  const [s, setS] = useState<DemoState>({
    typed: "",
    showUser: false,
    thinking: false,
    replied: false,
    scen: 0,
  });
  const dead = useRef(false);

  useEffect(() => {
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!autoplay || reduced) {
      setS(SETTLED);
      return;
    }

    dead.current = false;
    const timers: number[] = [];
    const wait = (ms: number): Promise<void> =>
      new Promise((res) => {
        timers.push(window.setTimeout(res, ms));
      });

    const run = async (): Promise<void> => {
      let scen = 0;
      for (;;) {
        if (dead.current) return;
        const sp = speed;
        const sc = SCENARIOS[scen % SCENARIOS.length];
        if (!sc) return;
        setS({ typed: "", showUser: false, thinking: false, replied: false, scen });
        await wait(800 / sp);
        for (let i = 1; i <= sc.q.length; i += 1) {
          if (dead.current) return;
          setS((st) => ({ ...st, typed: sc.q.slice(0, i) }));
          await wait(32 / sp);
        }
        await wait(500 / sp);
        setS((st) => ({ ...st, showUser: true, typed: "" }));
        await wait(400 / sp);
        setS((st) => ({ ...st, thinking: true }));
        await wait(1100 / sp);
        setS((st) => ({ ...st, thinking: false, replied: true }));
        await wait(5400 / sp);
        scen += 1;
      }
    };
    void run();

    return () => {
      dead.current = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [autoplay, speed]);

  const sc = SCENARIOS[s.scen % SCENARIOS.length] ?? SCENARIOS[0]!;
  const hasCard = sc.card !== null;
  const badgeText = hasCard ? "Backed by stock" : "General advice";
  const badgeBg = hasCard ? "rgba(22,69,63,0.09)" : "rgba(199,90,52,0.12)";
  const badgeColor = hasCard ? "#16453F" : "#A34A22";
  const typedEmpty = s.typed.length === 0;
  const caretOn = s.typed.length > 0;

  return (
    <div
      style={{
        background: "#FFFDF8",
        border: "1px solid rgba(23,32,29,0.1)",
        borderRadius: 22,
        boxShadow: "0 34px 80px -34px rgba(22,69,63,0.45)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 22px",
          borderBottom: "1px solid rgba(23,32,29,0.08)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 10, height: 10, background: "#16453F", transform: "rotate(45deg)" }} />
          {/* A Latin logotype: the world's wordmark face, through its token. */}
          <span
            style={{
              fontFamily: "var(--gd-wordmark)",
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            GardEn Assistant
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#4A7A5C" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#4A7A5C" }} />
          <span>live inventory</span>
        </div>
      </div>

      <div
        style={{
          height: 396,
          padding: 22,
          display: "flex",
          flexDirection: "column",
          gap: 14,
          overflow: "hidden",
        }}
      >
        {s.showUser && (
          <div
            style={{
              alignSelf: "flex-end",
              maxWidth: "82%",
              background: "#16453F",
              color: "#F0E9DA",
              padding: "13px 17px",
              borderRadius: "16px 16px 4px 16px",
              fontSize: 14.5,
              lineHeight: 1.5,
            }}
          >
            {sc.q}
          </div>
        )}

        {s.thinking && (
          <div
            style={{
              alignSelf: "flex-start",
              background: "#F2ECDD",
              padding: "14px 18px",
              borderRadius: "16px 16px 16px 4px",
              display: "flex",
              gap: 5,
            }}
          >
            {[0, 0.2, 0.4].map((d) => (
              <span
                key={d}
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "#8B8474",
                  animation: `gdots 1.2s infinite ${d}s`,
                }}
              />
            ))}
          </div>
        )}

        {s.replied && (
          <div
            style={{
              alignSelf: "flex-start",
              maxWidth: "92%",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{
                background: "#F2ECDD",
                padding: "13px 17px",
                borderRadius: "16px 16px 16px 4px",
                fontSize: 14.5,
                lineHeight: 1.55,
                color: "#2B2A24",
              }}
            >
              {sc.a}
            </div>
            {sc.card && (
              <div
                style={{
                  background: "#FFFDF8",
                  border: "1px solid rgba(23,32,29,0.12)",
                  borderRadius: 14,
                  padding: "14px 16px",
                  display: "flex",
                  gap: 14,
                  alignItems: "center",
                }}
              >
                <img
                  src="/landing/cat-drymix.png"
                  alt=""
                  style={{ width: 54, height: 54, borderRadius: 10, flexShrink: 0, objectFit: "cover" }}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 14.5 }}>{sc.card.name}</span>
                  <span style={{ fontSize: 13, color: "#6E6A5E" }}>{sc.card.stall}</span>
                  <span style={{ fontWeight: 700, fontSize: 14.5, color: "#16453F" }}>
                    {sc.card.price}
                  </span>
                </div>
              </div>
            )}
            <div
              style={{
                alignSelf: "flex-start",
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                fontSize: 11.5,
                fontWeight: 600,
                letterSpacing: "0.04em",
                padding: "5px 11px",
                borderRadius: 999,
                background: badgeBg,
                color: badgeColor,
              }}
            >
              <span style={{ width: 6, height: 6, transform: "rotate(45deg)", background: badgeColor }} />
              <span>{badgeText}</span>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          margin: "0 18px 18px",
          border: "1.5px solid rgba(22,69,63,0.35)",
          borderRadius: 999,
          padding: "13px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          background: "#FFFDF8",
        }}
      >
        <span
          style={{
            fontSize: 14.5,
            color: "#2B2A24",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {s.typed}
          {caretOn && (
            <span
              style={{
                display: "inline-block",
                width: 2,
                height: 16,
                background: "#16453F",
                marginLeft: 2,
                verticalAlign: -3,
                animation: "gblink 1s infinite",
              }}
            />
          )}
          {typedEmpty && <span style={{ color: "#9A937F" }}>Ask about your renovation…</span>}
        </span>
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: "#16453F",
            color: "#F0E9DA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          ↑
        </span>
      </div>
    </div>
  );
}

export default DemoPanel;
